import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import {
	BOOK_INFO_DEFAULTS,
	ANGLE_LINES,
	MEASUREMENT_UNITS,
	MEASUREMENT_UNIT,
} from '../config/constants';
import {
	getHeightCM,
	mmToPoints,
	getHeightInchPrimary,
	getHeightInchFraction,
} from '../utils/numbers';

const CUT_LINE_COLOR = '#3D3D3D';

const BuildPattern = async (props) => {
	return new Promise(async (resolve, reject) => {
		const {
			angleLine,
			isInkSaver,
			imagePrimaryDataURL,
			imageSecondaryDataURL,
			measurementUnit,
			subscribed = false,
		} = props;
		let { height, lastOddPage, extraFrontPages, extraBackPages } = props;

		//input Validation
		height = Number(height);
		lastOddPage = Number(lastOddPage);
		extraFrontPages = Number(extraFrontPages);
		extraBackPages = Number(extraBackPages);
		if (
			height > BOOK_INFO_DEFAULTS.MAX_HEIGHT ||
			lastOddPage > BOOK_INFO_DEFAULTS.MAX_LAST_ODD_PAGE
		) {
			return null;
		}
		const numberOfFrontStrips = extraFrontPages;
		const numberOfBackStrips = extraBackPages;
		const numberOfNumberedStrips = (lastOddPage + 1) / 2;
		const totalNumberOfStrips =
			numberOfFrontStrips + numberOfNumberedStrips + numberOfBackStrips;

		const { Jimp, blobStream } = window;
		const leftMargin = 36;
		const topMargin = height > 260 ? 0 : 27; // If height is greater than 260mm, start strips at the top of the page
		const bottomMargin = 756;
		const docWidth = 540;
		const stripsPerPage = isInkSaver ? 12 : 10;
		const stripWidth = docWidth / stripsPerPage;
		const stripHeight = mmToPoints(height);
		const rowGap = 30; // Gap between rows to avoid interference
		const stripOptions = {
			width: stripWidth / (imageSecondaryDataURL ? 4 : 2),
			height: stripHeight,
		};

		const maxRowsPerPage = Math.max(
			1,
			Math.floor((bottomMargin - topMargin) / (stripHeight + rowGap)),
		);

		const doc = new PDFDocument();
		const font = await fetch(
			`${process.env.PUBLIC_URL}/fonts/Nunito-Regular.ttf`,
		);
		const arrayBuffer = await font.arrayBuffer();
		doc.registerFont('Nunito', arrayBuffer);
		doc.font('Nunito');
		const stream = doc.pipe(blobStream());
		stream.on('finish', () => {
			const url = stream.toBlobURL('application/pdf');
			const blob = stream.toBlob('application/pdf');
			resolve({ url, blob });
		});

		//Convert base64 image to Jimp image
		const jimpPrimaryImage = await Jimp.read(imagePrimaryDataURL);
		let jimpSecondaryImage;
		if (imageSecondaryDataURL) {
			jimpSecondaryImage = await Jimp.read(imageSecondaryDataURL);
		}

		//Preview Text
		if (!subscribed) {
			doc.fontSize(11)
				.fillColor('black')
				.text('This is only a partial pattern', leftMargin, 10, {
					width: docWidth,
					align: 'center',
				});
			doc.fontSize(8)
				.fillColor('black')
				.text(
					'Subscribe now to create unlimited patterns',
					leftMargin,
					30,
					{
						width: docWidth,
						align: 'center',
					},
				);
			doc.fontSize(8)
				.fillColor('black')
				.text('or purchase this pattern individually', leftMargin, 40, {
					width: docWidth,
					align: 'center',
				});
		}

		//Logo
		const logo = await Jimp.read(`${process.env.PUBLIC_URL}/Logo.png`);
		doc.image(await logo.getBase64Async(Jimp.AUTO), docWidth / 2 + 10, 45, {
			width: 50,
			height: 50,
			align: 'center',
		});
		doc.fontSize(11)
			.fillColor('black')
			.text('ForeEdgeBookArt.com', leftMargin, 90, {
				width: docWidth,
				align: 'center',
			});

		//Preview page
		if (imageSecondaryDataURL) {
			const maxDimension = 200; // Maximum width or height for each image

			// Load and get properties of the primary image
			const originalPrimaryWidth = jimpPrimaryImage.bitmap.width;
			const originalPrimaryHeight = jimpPrimaryImage.bitmap.height;
			const primaryAspectRatio =
				originalPrimaryWidth / originalPrimaryHeight;
			let primaryWidthToFit = maxDimension;
			let primaryHeightToFit = primaryWidthToFit / primaryAspectRatio;
			if (primaryHeightToFit > maxDimension) {
				primaryHeightToFit = maxDimension;
				primaryWidthToFit = primaryHeightToFit * primaryAspectRatio;
			}

			// Calculate center position for the primary image
			const primaryCenterX =
				leftMargin + (docWidth / 4 - primaryWidthToFit / 2);
			const verticalRange = 310 - 110; // Total vertical space available
			const primaryY = 110 + (verticalRange / 2 - primaryHeightToFit / 2); // Centering vertically within 110 to 310

			// Load and get properties of the secondary image
			const originalSecondaryWidth = jimpSecondaryImage.bitmap.width;
			const originalSecondaryHeight = jimpSecondaryImage.bitmap.height;
			const secondaryAspectRatio =
				originalSecondaryWidth / originalSecondaryHeight;
			let secondaryWidthToFit = maxDimension;
			let secondaryHeightToFit =
				secondaryWidthToFit / secondaryAspectRatio;
			if (secondaryHeightToFit > maxDimension) {
				secondaryHeightToFit = maxDimension;
				secondaryWidthToFit =
					secondaryHeightToFit * secondaryAspectRatio;
			}

			// Calculate center position for the secondary image
			const secondaryCenterX =
				leftMargin + (3 * docWidth) / 4 - secondaryWidthToFit / 2;
			const secondaryY =
				110 + (verticalRange / 2 - secondaryHeightToFit / 2); // Centering vertically within 110 to 310

			// Place the primary image
			doc.image(
				await jimpPrimaryImage.getBase64Async(Jimp.AUTO),
				primaryCenterX,
				primaryY,
				{ width: primaryWidthToFit, height: primaryHeightToFit },
			);

			// Place the secondary image
			doc.image(
				await jimpSecondaryImage.getBase64Async(Jimp.AUTO),
				secondaryCenterX,
				secondaryY,
				{ width: secondaryWidthToFit, height: secondaryHeightToFit },
			);
		} else {
			const maxHeight = 300;
			const maxWidth = docWidth - 2 * leftMargin; // Define the maximum width the image can have without exceeding the page's width

			const originalImageHeight = jimpPrimaryImage.bitmap.height;
			const originalImageWidth = jimpPrimaryImage.bitmap.width;
			const aspectRatio = originalImageWidth / originalImageHeight;

			// Calculate initial width and height based on max height and aspect ratio
			let widthToFit = maxHeight * aspectRatio;
			let heightToFit = maxHeight;

			// Adjust width and height if the width exceeds the maximum allowable width
			if (widthToFit > maxWidth) {
				widthToFit = maxWidth; // Adjust width to fit within the page
				heightToFit = widthToFit / aspectRatio; // Recalculate height to maintain aspect ratio
			}

			// Center the image horizontally
			const centerX = leftMargin + (docWidth / 2 - widthToFit / 2);

			// Define vertical bounds and calculate the center point
			const topBound = 110;
			const bottomBound = 410;
			const verticalCenter = topBound + (bottomBound - topBound) / 2;

			// Calculate y-coordinate to center the image vertically within the bounds
			const centerY = verticalCenter - heightToFit / 2;

			doc.image(
				await jimpPrimaryImage.getBase64Async(Jimp.AUTO),
				centerX,
				centerY, // Use centerY for vertical centering
				{
					width: widthToFit,
					height: heightToFit,
				},
			);
		}

		//Pattern Information
		const heightLabelYAxis = imageSecondaryDataURL ? 320 : 420;
		const measurementUnitLabel = MEASUREMENT_UNITS.find(
			(unit) => unit.value === measurementUnit,
		).label;
		let heightLabel;
		if (measurementUnit === MEASUREMENT_UNIT.cm) {
			heightLabel = getHeightCM(height) + measurementUnitLabel;
		} else if (measurementUnit === MEASUREMENT_UNIT.inch) {
			const fraction = getHeightInchFraction(height).label;
			const fractionPart = fraction !== '-' ? ` ${fraction}` : '';
			heightLabel = `${getHeightInchPrimary(height)}${fractionPart} ${measurementUnitLabel}`;
		}
		//Pattern Height
		doc.fontSize(11)
			.fillColor('black')
			.text(`Height: ${heightLabel}`, leftMargin, heightLabelYAxis, {
				width: docWidth,
				align: 'center',
			});
		//Last odd page
		doc.fontSize(11)
			.fillColor('black')
			.text(
				`Last Odd Page: ${lastOddPage}`,
				leftMargin,
				heightLabelYAxis + 15,
				{ width: docWidth, align: 'center' },
			);

		//#region Step-by-step Instruction Grid with Styled Text

		// Font registration (if not already registered elsewhere)
		const nunitoBoldBuffer = await fetch(
			`${process.env.PUBLIC_URL}/fonts/Nunito-Bold.ttf`,
		).then((res) => res.arrayBuffer());
		const nunitoRegularBuffer = await fetch(
			`${process.env.PUBLIC_URL}/fonts/Nunito-Regular.ttf`,
		).then((res) => res.arrayBuffer());

		doc.registerFont('Nunito-Bold', nunitoBoldBuffer);
		doc.registerFont('Nunito-Regular', nunitoRegularBuffer);

		// Instructional assets
		const instructionAssets = [
			'text',
			`${process.env.PUBLIC_URL}/images/1-Print.jpg`,
			`${process.env.PUBLIC_URL}/images/2-Cut.jpg`,
			`${process.env.PUBLIC_URL}/images/3-Fold.jpg`,
			`${process.env.PUBLIC_URL}/images/4-Apply.jpg`,
			`${process.env.PUBLIC_URL}/images/5-Display.jpg`,
		];

		// Blurbs
		const blurbs = {
			1: {
				title: 'Print this pattern',
				subtitle:
					"Print in portrait mode at 100% scale or at 'Actual Size' using an inkjet printer",
			},
			2: {
				title: 'Cut the strips',
				subtitle:
					'Trim the top and bottom then cut the strips along the solid lines',
			},
			3: {
				title: 'Fold the strips',
				subtitle: 'Fold each strip in half lengthwise',
			},
			4: {
				title: 'Apply the strips',
				subtitle:
					'Attach strips to the page using tape or glue, aligning the strip number with the page number',
			},
			5: {
				title: 'Display your book!',
				subtitle:
					'Once all strips are applied, admire your finished book!',
			},
		};

		// Grid config
		const gridTopY = heightLabelYAxis + 60;
		const imageWidth = 150;
		const imageHeight = 70;
		const columnGap = 20;
		const instructionRowGap = 30;
		const extraRowPadding = 10;
		const columns = 3;

		const totalGridWidth = columns * imageWidth + (columns - 1) * columnGap;
		const gridStartX = leftMargin + (docWidth - totalGridWidth) / 2;

		for (let index = 0; index < 6; index++) {
			const col = index % columns;
			const row = Math.floor(index / columns);
			const x = gridStartX + col * (imageWidth + columnGap);
			const y =
				gridTopY +
				row * (imageHeight + instructionRowGap) +
				(row === 1 ? extraRowPadding : 0);

			const asset = instructionAssets[index];

			if (asset === 'text') {
				const blockYOffset = 25; // Increase to move down more
				const centerY = y + imageHeight / 2 - 20 + blockYOffset;

				// Book icon
				const icon = await Jimp.read(
					`${process.env.PUBLIC_URL}/images/book-icon-black.png`,
				);
				const iconBase64 = await icon.getBase64Async(Jimp.AUTO);
				const iconSize = 20;
				const iconX = x + (imageWidth - iconSize) / 2;
				const iconY = centerY - 18;

				doc.image(iconBase64, iconX, iconY, {
					width: iconSize,
					height: iconSize,
				});

				doc.font('Nunito-Bold')
					.fontSize(12)
					.fillColor('#2C3E50')
					.text('Step by Step:', x, centerY, {
						width: imageWidth,
						align: 'center',
					});

				doc.font('Nunito-Regular')
					.fillColor('black')
					.text('Turn this pattern into', x, centerY + 14, {
						width: imageWidth,
						align: 'center',
					});

				doc.text('Fore Edge Book Art', x, centerY + 28, {
					width: imageWidth,
					align: 'center',
				});
			} else {
				const img = await Jimp.read(asset);
				const imgBase64 = await img.getBase64Async(Jimp.AUTO);

				const originalWidth = img.bitmap.width;
				const originalHeight = img.bitmap.height;
				const scale = imageHeight / originalHeight;
				const scaledWidth = originalWidth * scale;
				const offsetX = x + (imageWidth - scaledWidth) / 2;

				doc.image(imgBase64, offsetX, y, {
					width: scaledWidth,
					height: imageHeight,
				});

				const badgeNumber = index;
				const badgeRadius = 12;
				const badgeX = x + 2;
				const badgeY = y + 2;
				const verticalTextOffset = 5;

				doc.save()
					.fillOpacity(0.6)
					.circle(
						badgeX + badgeRadius,
						badgeY + badgeRadius,
						badgeRadius,
					)
					.fillAndStroke('#DDD', '#666')
					.restore();

				doc.fillColor('#000')
					.fontSize(10)
					.font('Nunito-Bold')
					.text(
						badgeNumber.toString(),
						badgeX,
						badgeY + verticalTextOffset,
						{
							width: badgeRadius * 2,
							align: 'center',
						},
					);

				const blurb = blurbs[badgeNumber];
				if (blurb) {
					const textTop = y + imageHeight + 2;

					doc.fontSize(9)
						.fillColor('#2C3E50')
						.font('Nunito-Bold')
						.text(blurb.title, x, textTop, {
							width: imageWidth,
							align: 'center',
						});

					doc.fontSize(8)
						.fillColor('#555555')
						.font('Nunito-Regular')
						.text(blurb.subtitle, x, textTop + 12, {
							width: imageWidth,
							align: 'center',
						});
				}
			}
		}

		//#endregion

		// Pattern

		doc.addPage();

		//squish image horizontally
		const resizedPrimaryImage = await jimpPrimaryImage.resize(
			totalNumberOfStrips,
			jimpPrimaryImage.bitmap.height,
		);
		let resizedSecondaryImage;
		if (imageSecondaryDataURL) {
			resizedSecondaryImage = await jimpSecondaryImage.resize(
				totalNumberOfStrips,
				jimpSecondaryImage.bitmap.height,
			);
		}

		let stripNumber = 0;
		let rowNumber = 0;

		for (let i = 0; i < totalNumberOfStrips; i++) {
			const x = leftMargin + stripNumber * stripWidth;
			const y = topMargin + rowNumber * (stripHeight + rowGap);
			const number = i + 1;
			let isExtraPage = false;
			let stripNumberLabel;

			// Dealing with extra front pages
			if (numberOfFrontStrips > 0 && number <= numberOfFrontStrips) {
				isExtraPage = true;
				stripNumberLabel = number;
			}
			// Dealing with extra back pages
			else if (
				numberOfBackStrips > 0 &&
				number > numberOfFrontStrips + numberOfNumberedStrips
			) {
				isExtraPage = true;
				stripNumberLabel =
					number - numberOfFrontStrips - numberOfNumberedStrips;
			}
			// Dealing with numbered pages
			else {
				const oddPageNumber = (number - numberOfFrontStrips) * 2 - 1;
				stripNumberLabel = oddPageNumber;
			}

			//Primary Strip
			//Read image - for some reason this needs to be done each iteration
			const resizedPrimaryJimpImage =
				await Jimp.read(resizedPrimaryImage);
			const stripPrimary = await resizedPrimaryJimpImage
				.crop(i, 0, 1, jimpPrimaryImage.bitmap.height)
				.getBase64Async(Jimp.AUTO);

			//Secondary Strip
			let resizedSecondaryJimpImage;
			let stripSecondary;
			if (imageSecondaryDataURL) {
				resizedSecondaryJimpImage = await Jimp.read(
					resizedSecondaryImage,
				);
				stripSecondary = await resizedSecondaryJimpImage
					.crop(i, 0, 1, jimpSecondaryImage.bitmap.height)
					.getBase64Async(Jimp.AUTO);
			}

			//Cut line at the beginning of a page
			if (stripNumber === 0) {
				//Cut line
				doc.lineCap('butt')
					.lineWidth(1)
					.moveTo(x, y)
					.lineTo(x, y + stripHeight)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
			}

			//Sideways strip number
			const stripNumberOffset = angleLine === ANGLE_LINES.NONE ? 15 : 25;
			doc.rotate(90, { origin: [x, y] });
			doc.fontSize(8)
				.fillColor(isExtraPage ? 'grey' : 'black')
				.text('< ' + stripNumberLabel, x + stripNumberOffset, y - 10, {
					width: 40,
					align: 'left',
					oblique: isExtraPage ? true : false,
				});
			doc.rotate(-90, { origin: [x, y] });

			//Watermark
			if (stripNumber % 4 === 0) {
				doc.rotate(90, { origin: [x, y] });
				doc.fontSize(7)
					.fillColor('#7A7A7A')
					.text(
						'ForeEdgeBookArt.com',
						x + (stripHeight / 2 - 50),
						y - 10,
						{
							width: 100,
							align: 'center',
						},
					);
				doc.rotate(-90, { origin: [x, y] });
			}

			//Top Fold Line
			doc.lineCap('butt')
				.lineWidth(2)
				.undash()
				.moveTo(x + stripWidth / 2, y - 20)
				.lineTo(x + stripWidth / 2, y - 10)
				.strokeColor('black')
				.stroke();

			// Top cut line
			doc.lineCap('butt')
				.lineWidth(1)
				.undash()
				.moveTo(x, y)
				.lineTo(x + stripWidth, y)
				.strokeColor(CUT_LINE_COLOR)
				.stroke();

			// Bottom cut line
			doc.lineCap('butt')
				.lineWidth(1)
				.undash()
				.moveTo(x, y + stripHeight)
				.lineTo(x + stripWidth, y + stripHeight)
				.strokeColor(CUT_LINE_COLOR)
				.stroke();

			//Image strip
			doc.image(stripPrimary, x + stripWidth / 4, y, stripOptions);
			if (imageSecondaryDataURL) {
				doc.image(
					stripSecondary,
					x + stripWidth / 4 + stripOptions.width,
					y,
					stripOptions,
				);
			}

			//45deg cut lines
			if (
				[ANGLE_LINES.TOPONLY, ANGLE_LINES.TOPANDBOTTOM].includes(
					angleLine,
				)
			) {
				//45deg cut line at top left of strip
				doc.lineCap('butt')
					.lineWidth(1)
					.undash()
					.moveTo(x, y + stripWidth / 2)
					.lineTo(x + stripWidth / 2 - 5, y + 5)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
				//45deg cut line at top right of strip
				doc.lineCap('butt')
					.lineWidth(1)
					.undash()
					.moveTo(x + stripWidth, y + stripWidth / 2)
					.lineTo(x + stripWidth / 2 + 5, y + 5)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
			}
			if (
				[ANGLE_LINES.BOTTOMONLY, ANGLE_LINES.TOPANDBOTTOM].includes(
					angleLine,
				)
			) {
				//45deg cut line at bottom left of strip
				doc.lineCap('butt')
					.lineWidth(1)
					.undash()
					.moveTo(x, y + stripHeight - stripWidth / 2)
					.lineTo(x + stripWidth / 2 - 5, y + stripHeight - 5)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
				//45deg cut line at bottom right of strip
				doc.lineCap('butt')
					.lineWidth(1)
					.undash()
					.moveTo(x + stripWidth, y + stripHeight - stripWidth / 2)
					.lineTo(x + stripWidth / 2 + 5, y + stripHeight - 5)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
			}

			//Bottom Fold Line
			doc.lineCap('butt')
				.lineWidth(2)
				.undash()
				.moveTo(x + stripWidth / 2, y + stripHeight + 10)
				.lineTo(x + stripWidth / 2, y + stripHeight + 20)
				.strokeColor('black')
				.stroke();

			stripNumber++;

			//Cut line between image strips
			if (stripNumber < stripsPerPage && i + 1 !== totalNumberOfStrips) {
				//Cut line
				doc.lineCap('butt')
					.lineWidth(1)
					.moveTo(x + stripWidth, y)
					.lineTo(x + stripWidth, y + stripHeight)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
			}

			//Cut line at the end of a row and there are more strips to be added or this is the last strip
			if (
				stripNumber === stripsPerPage ||
				i + 1 === totalNumberOfStrips
			) {
				//Cut line
				doc.lineCap('butt')
					.lineWidth(1)
					.moveTo(x + stripWidth, y)
					.lineTo(x + stripWidth, y + stripHeight)
					.strokeColor(CUT_LINE_COLOR)
					.stroke();
			}

			// Move to the next row if the current row is full
			if (stripNumber >= stripsPerPage) {
				stripNumber = 0;
				rowNumber++;
			}

			// Add new page if subscribed, no more rows will fit, and there are still more image strips to add
			if (
				subscribed &&
				rowNumber >= maxRowsPerPage &&
				i + 1 !== totalNumberOfStrips
			) {
				stripNumber = 0;
				rowNumber = 0;
				doc.addPage();
			}

			// If not subscribed, don't add any more strip pages
			if (!subscribed && rowNumber >= maxRowsPerPage) {
				break;
			}
		}
		doc.end();
	});
};

export default BuildPattern;
